<script setup lang="ts">
  import type { CartItem } from '@/types/Cart'
  import { useCart, type CartSuggestion } from '@/composables/useCart'

  useHead({ title: 'cart' })
  const {
    cart,
    cartInput,
    cartInputRef,
    cartInputExpanded,
    cartInputPlaceholder,
    suggestions,
    addItem,
    toggleComplete,
    cartInputFocusIn,
    cartInputFocusOut,
  } = useCart()

  function handleSubmit() {
    if (!cartInput.value.name) return
    addItem(cartInput.value)
    cartInput.value = { name: '' }
  }

  function handleSelectSuggestion(suggestion: CartSuggestion) {
    if (!suggestion.name) return

    if (suggestion.cartId) {
      const item = cart.value.find(({ id }) => id === suggestion.cartId) as CartItem
      toggleComplete(item)
      cartInput.value = { name: '' }
      return
    }

    addItem({ name: suggestion.name })
    cartInput.value = { name: '' }
  }
</script>

<template>
  <form class="cart__newItemForm relative z-10 mt-4" @submit.prevent="handleSubmit()">
    <div class="flex items-stretch w-full">
      <div
        class="cart__newItemInputWrapper w-full"
        :class="{ 'cart__newItemInputWrapper--expanded': cartInputExpanded }"
      >
        <input
          ref="cartInputRef"
          v-model="cartInput.name"
          type="text"
          class="cart__newItemInput"
          :placeholder="cartInputPlaceholder"
          @focusin="cartInputFocusIn"
          @focusout="cartInputFocusOut"
        />
        <button v-if="cartInput.name.length" type="button" class="cart__newItemInputClear" @click="cartInput.name = ''">
          <i class="far fa-times" />
        </button>
      </div>
      <button type="submit" class="cart__newItemAddButton" :disabled="!cartInput.name.length">
        <i class="far fa-plus" />
        add
      </button>
    </div>

    <Transition name="slide-fade">
      <TransitionGroup
        v-if="Object.values(suggestions).some((list) => list.length > 0)"
        name="list"
        tag="dl"
        class="cart__suggestions"
      >
        <template v-for="(list, title) in suggestions">
          <dt v-if="list.length" :key="title">{{ title }}</dt>
          <dd
            v-for="suggestion in list"
            :key="JSON.stringify(suggestion)"
            class="cart__suggestionsItem"
            tabindex="0"
            :title="suggestion.name"
            @click="handleSelectSuggestion(suggestion)"
            @keydown.enter="handleSelectSuggestion(suggestion)"
          >
            {{ suggestion.name }}
          </dd>
        </template>
      </TransitionGroup>
    </Transition>
  </form>
</template>

<style lang="sass">
  .cart
    &__newItem
      &InputWrapper
        position: relative
        display: inline-flex
        width: 100%
        max-width: calc(var(--page-max-width) - 80px - 4px)
        transition: 150ms ease-in-out
        .cart__newItemInput
          border-color: transparent
          padding: 5px 0
        ~ .cart__newItemAddButton
          opacity: 0
          transform: translateX(-8px)
        &--expanded
          .cart__newItemInput
            padding: 5px 26px 5px 12px
            border-color: theme('colors.juss.purple.400')
          ~ .cart__newItemAddButton
            opacity: 1
            transform: translateX(0)

      &Input
        font-family: "Quicksand", sans-serif
        font-size: 16px
        padding: 5px 26px 5px 12px
        border: 2px solid theme('colors.juss.purple.400')
        border-radius: 3px
        width: 100%
        color: theme('colors.juss.purple.600')
        transition: 150ms ease-in-out
        font-weight: 900
        border-color: transparent
        padding: 5px 0
        &::placeholder
          color: theme('colors.juss.purple.400')

      &InputClear
        position: absolute
        right: 0
        top: 0
        bottom: 0
        -webkit-appearance: none
        background: transparent
        border: none
        color: #d2c2e2
        padding: 0 12px
        font-size: 16px
        cursor: pointer
        transition: 150ms ease-in-out
        &:hover
          color: #b4a5c3
        &:focus
          outline: none

      &AddButton
        width: 80px
        color: theme('colors.juss.purple.400')
        background-color: #d9cfe4
        border-radius: 3px
        text-transform: uppercase
        font-size: 0.8em
        font-weight: 900
        cursor: pointer
        padding: 8px 12px
        margin-left: 4px
        appearance: none
        border: none
        white-space: nowrap
        transition: 150ms ease-in-out
        &:hover
          background-color: #b4a5c3
        &:disabled
          cursor: not-allowed
          color: #b4a5c3
          background-color: #d9cfe4

    &__suggestions
      margin-top: .5rem
      border: 1px solid theme('colors.slate.300')
      border-radius: 3px
      background-color: white
      padding: .5rem 0
      max-height: 300px
      width: 100%
      max-width: var(--page-max-width)
      overflow: auto
      position: absolute
      transition: 150ms ease-in-out
      dt
        padding: 0 .5rem
        font-weight: bolder
        font-family: 'Courier New', Courier, monospace
        text-transform: uppercase
        color: theme('colors.slate.400')
        ~ dt
          margin-top: 1rem
      &Item
        padding: .125rem .5rem
        cursor: pointer
        overflow: hidden
        white-space: nowrap
        text-overflow: ellipsis
        transition: 150ms ease-in-out
        &:hover, &:focus
          background-color: theme('colors.slate.100')
</style>
